import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createPrompt } from '../../api/data-connectors/create-prompt'
import { useNotification } from '../../hooks/useNotification'
import { DEFAULT_MODEL } from '../../types'
import { importPromptsFromJsonFiles } from './prompts-import.utils'

interface UseImportPromptsParams {
  refetch: () => Promise<unknown>
}

function isJsonFile(file: File): boolean {
  const lowerFileName = file.name.toLowerCase()

  return (
    lowerFileName.endsWith('.json') || file.type === 'application/json' || file.type === 'text/json'
  )
}

export function useImportPrompts({ refetch }: UseImportPromptsParams) {
  const [isImportingPrompts, setIsImportingPrompts] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const { notify } = useNotification()
  const { t } = useTranslation()

  const cancelImportPrompts = useCallback(() => {
    const activeController = abortControllerRef.current

    if (!activeController) {
      return
    }

    activeController.abort()
    setIsImportingPrompts(false)
  }, [])

  const importPrompts = useCallback(
    async (files: File[]) => {
      if (files.length === 0) {
        return
      }

      const validFiles = files.filter(isJsonFile)
      const hasInvalidFiles = validFiles.length !== files.length

      if (hasInvalidFiles) {
        notify({
          isSuccess: false,
          text: t('prompts.importInvalidFileTypeToast'),
          position: 'top',
        })
      }

      if (validFiles.length === 0) {
        return
      }

      const abortController = new AbortController()
      abortControllerRef.current = abortController
      setIsImportingPrompts(true)

      try {
        const { importedCount, failedCount, failedPrompts, isCancelled } =
          await importPromptsFromJsonFiles({
            files: validFiles,
            signal: abortController.signal,
            createPrompt: async (prompt) => {
              await createPrompt({
                userPrompt: prompt.userPrompt,
                llmModel: DEFAULT_MODEL,
                title: prompt.title,
                signal: abortController.signal,
              })
            },
          })

        if (isCancelled) {
          notify({
            isSuccess: false,
            text: t('prompts.importCancelledToast'),
            position: 'top',
          })
          return
        }

        const failedPromptNames = failedPrompts
          .map((prompt) => prompt.title.trim())
          .filter((label) => label.length > 0)
        const failedItemsSuffix = failedPromptNames.length
          ? ` ${t('prompts.importFailedItems', { items: failedPromptNames.join(', ') })}`
          : ''

        if (importedCount > 0) {
          try {
            // Imported prompts make the current prompt lists stale, so refresh all prompt tabs.
            await refetch()
          } catch (error) {
            console.error('Refetch after prompt import failed:', error)
          }
        }

        if (importedCount === 0) {
          notify({
            isSuccess: false,
            text: `${t('prompts.importFailureToast')}${failedItemsSuffix}`,
            position: 'top',
          })
          return
        }

        notify({
          isSuccess: failedCount === 0,
          text:
            failedCount === 0
              ? t('prompts.importSuccessToast', { count: importedCount })
              : `${t('prompts.importPartialToast', {
                  success: importedCount,
                  failed: failedCount,
                })}${failedItemsSuffix}`,
          position: 'top',
        })
      } catch (error) {
        if (abortController.signal.aborted) {
          notify({
            isSuccess: false,
            text: t('prompts.importCancelledToast'),
            position: 'top',
          })
          return
        }

        console.error('Import prompts failed:', error)
        notify({
          isSuccess: false,
          text: t('prompts.importFailureToast'),
          position: 'top',
        })
      } finally {
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null
          setIsImportingPrompts(false)
        }
      }
    },
    [notify, refetch, t]
  )

  return {
    importPrompts,
    cancelImportPrompts,
    isImportingPrompts,
  }
}
